<section class="w-full text-fg">
  <div class="mb-5 flex flex-col items-start justify-between gap-5 md:flex-row md:items-end">
    <div>
      <h1 class="m-0 font-heading text-[2rem] font-extrabold tracking-tight text-fg">{{ pageTitle }}</h1>
      <p class="m-0 mt-1 text-sm font-medium text-muted">{{ pageSubtitle }}</p>
    </div>
    <div class="flex flex-wrap gap-1 rounded-xl border border-border bg-surface-muted p-1">
      @for (p of periods; track p.key) {
        <button
          type="button"
          class="cursor-pointer rounded-lg border-0 px-3 py-2 text-[0.6875rem] font-bold tracking-wide transition-colors"
          [class]="selectedPeriod() === p.key ? 'bg-brand text-white shadow-sm' : 'bg-transparent text-muted hover:text-fg'"
          (click)="
            p.key === 'custom'
              ? onPeriodChange('custom', startDate() ?? undefined, endDate() ?? undefined)
              : onPeriodChange(p.key)
          "
        >
          {{ p.label }}
        </button>
      }
    </div>
  </div>

  @if (selectedPeriod() === 'custom') {
    <div class="mb-6 flex flex-wrap items-center gap-2.5 text-xs">
      <label for="startDate" class="font-semibold text-fg-secondary">Start date</label>
      <input uiInput size="sm" class="!w-auto" type="date" id="startDate" [(ngModel)]="startDate" (change)="onPeriodChange('custom', startDate() ?? undefined, endDate() ?? undefined)" />
      <label for="endDate" class="font-semibold text-fg-secondary">End date</label>
      <input uiInput size="sm" class="!w-auto" type="date" id="endDate" [(ngModel)]="endDate" (change)="onPeriodChange('custom', startDate() ?? undefined, endDate() ?? undefined)" />
    </div>
  }

  @if (loading()) {
    <div class="flex flex-col items-center gap-3 py-12 text-muted">
      <ui-spinner />
      <p class="m-0">Loading dashboard data…</p>
    </div>
  } @else {
    @if (alerts(); as alertData) {
      <section class="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <ui-alert variant="danger" class="mb-0!">
          <div class="flex items-center gap-3.5">
            <ng-icon name="lucideTriangleAlert" size="22" />
            <div>
              <p class="m-0 text-[0.82rem] font-extrabold">{{ alertData.total_failed }} Failed Transactions</p>
              <p class="m-0 mt-0.5 text-[0.7rem] opacity-90">Immediate attention required for failed nodes</p>
            </div>
          </div>
        </ui-alert>
        <ui-alert variant="warning" class="mb-0!">
          <div class="flex items-center gap-3.5">
            <ng-icon name="lucideClock" size="22" />
            <div>
              <p class="m-0 text-[0.82rem] font-extrabold">{{ alertData.total_pending }} Transactions Pending</p>
              <p class="m-0 mt-0.5 text-[0.7rem] opacity-90">Currently processing in the system queue</p>
            </div>
          </div>
        </ui-alert>
      </section>
    }

    @if (feeSummary(); as feeData) {
      <section class="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        <ui-card [padded]="false">
          <div class="flex items-center justify-between gap-2.5 bg-linear-to-br from-primary to-brand px-6 py-4 text-white">
            <h2 class="m-0 text-[0.83rem] font-extrabold tracking-wider uppercase">Fee Revenue (Gross)</h2>
            <a [routerLink]="shellRoutes.commands('fees-hub')" class="text-xs font-bold text-white no-underline">Fees Hub →</a>
          </div>
          <div class="p-6">
            @if ((feeData.gross_gain_by_currency | keyvalue).length > 0) {
              <ul class="m-0 flex list-none flex-col gap-6 p-0">
                @for (entry of feeData.gross_gain_by_currency | keyvalue; track entry.key) {
                  <li class="grid grid-cols-[3rem_1fr_auto] items-center gap-2.5">
                    <span class="text-[0.68rem] font-bold tracking-wide text-muted uppercase">{{ entry.key }}</span>
                    <span class="col-start-3 text-[0.73rem] font-extrabold text-fg">{{ formatMoney(entry.value, entry.key) }}</span>
                  </li>
                }
              </ul>
            } @else {
              <p class="m-0 text-muted">No fees for selected period.</p>
            }
          </div>
        </ui-card>
        <ui-card [padded]="false">
          <div class="flex items-center justify-between gap-2.5 bg-linear-to-br from-primary to-brand px-6 py-4 text-white">
            <h2 class="m-0 text-[0.83rem] font-extrabold tracking-wider uppercase">Merchant Paid (All Fees)</h2>
            <span class="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold text-white">{{ getPeriodLabel() }}</span>
          </div>
          <div class="p-6">
            @if ((feeData.merchant_paid_by_currency | keyvalue).length > 0) {
              <ul class="m-0 flex list-none flex-col gap-6 p-0">
                @for (entry of feeData.merchant_paid_by_currency | keyvalue; track entry.key) {
                  <li class="grid grid-cols-[3rem_1fr_auto] items-center gap-2.5">
                    <span class="text-[0.68rem] font-bold tracking-wide text-muted uppercase">{{ entry.key }}</span>
                    <span class="col-start-3 text-[0.73rem] font-extrabold text-fg">{{ formatMoney(entry.value, entry.key) }}</span>
                  </li>
                }
              </ul>
            } @else {
              <p class="m-0 text-muted">No fees for selected period.</p>
            }
          </div>
        </ui-card>
      </section>
    }

    @if (summary(); as summaryData) {
      <section class="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3 min-[1201px]:grid-cols-5">
        @if (sectionFlags().showApplicationsSummary) {
          <a [routerLink]="shellRoutes.commands('manage', 'merchant-applications')" class="flex gap-3 rounded-xl border border-border bg-card p-5 text-inherit no-underline">
            <div class="grid size-[2.4rem] place-items-center rounded-xl bg-row-hover text-primary"><ng-icon name="lucideSettings" size="20" /></div>
            <div>
              <p class="m-0 text-[0.6875rem] font-bold tracking-widest text-muted uppercase">Applications</p>
              <p class="m-0 mt-1 text-[2rem] leading-none font-black tracking-tight text-fg">{{ summaryData.applications.total }}</p>
              <p class="m-0 mt-1 text-[0.69rem] font-bold text-success">+{{ getKpiTrend('Applications') ?? 0 | number: '1.0-1' }}% vs last month</p>
            </div>
          </a>
        }
        @if (sectionFlags().showUserSummary) {
          <a [routerLink]="shellRoutes.commands('manage', 'users')" class="flex gap-3 rounded-xl border border-border bg-card p-5 text-inherit no-underline">
            <div class="grid size-[2.4rem] place-items-center rounded-xl bg-row-hover text-primary"><ng-icon name="lucideUsers" size="20" /></div>
            <div>
              <p class="m-0 text-[0.6875rem] font-bold tracking-widest text-muted uppercase">Users</p>
              <p class="m-0 mt-1 text-[2rem] leading-none font-black tracking-tight text-fg">{{ summaryData.users.total }}</p>
              <p class="m-0 mt-1 text-[0.69rem] font-bold text-success">+{{ getKpiTrend('Users') ?? 0 | number: '1.0-1' }}% vs last month</p>
            </div>
          </a>
        }
        @if (sectionFlags().showWalletsSummary) {
          <a [routerLink]="shellRoutes.commands('manage', 'wallets')" class="flex gap-3 rounded-xl border border-border bg-card p-5 text-inherit no-underline">
            <div class="grid size-[2.4rem] place-items-center rounded-xl bg-row-hover text-primary"><ng-icon name="lucideBanknote" size="20" /></div>
            <div>
              <p class="m-0 text-[0.6875rem] font-bold tracking-widest text-muted uppercase">Wallets</p>
              <p class="m-0 mt-1 text-[2rem] leading-none font-black tracking-tight text-fg">{{ summaryData.wallets.total }}</p>
              <p class="m-0 mt-1 text-[0.69rem] font-bold text-success">+{{ getKpiTrend('Wallets') ?? 0 | number: '1.0-1' }}% vs last month</p>
            </div>
          </a>
        }
        @if (sectionFlags().showEntitiesSummary) {
          <a [routerLink]="shellRoutes.commands('manage', 'entities')" class="flex gap-3 rounded-xl border border-border bg-card p-5 text-inherit no-underline">
            <div class="grid size-[2.4rem] place-items-center rounded-xl bg-row-hover text-primary"><ng-icon name="lucideBookOpen" size="20" /></div>
            <div>
              <p class="m-0 text-[0.6875rem] font-bold tracking-widest text-muted uppercase">Entities</p>
              <p class="m-0 mt-1 text-[2rem] leading-none font-black tracking-tight text-fg">{{ summaryData.entities.total }}</p>
              <p class="m-0 mt-1 text-[0.69rem] font-bold text-success">+{{ getKpiTrend('Entities') ?? 0 | number: '1.0-1' }}% vs last month</p>
            </div>
          </a>
        }
        @if (sectionFlags().showTransactionMetrics && transactions(); as txData) {
          <div class="flex gap-3 rounded-xl border border-border bg-card p-5">
            <div class="grid size-[2.4rem] place-items-center rounded-xl bg-row-hover text-primary"><ng-icon name="lucideArrowLeftRight" size="20" /></div>
            <div>
              <p class="m-0 text-[0.6875rem] font-bold tracking-widest text-muted uppercase">Transactions</p>
              <p class="m-0 mt-1 text-[2rem] leading-none font-black tracking-tight text-fg">{{ formatCompact(getTransactionTypesTotal()) }}</p>
              <p class="m-0 mt-1 text-[0.69rem] font-bold text-success">
                +{{ getKpiTrend('Transactions') ?? getPeriodCountTrendPercent(getTransactionTypesTotal(), 0) ?? 0 | number: '1.0-1' }}% vs last month
              </p>
            </div>
          </div>
        }
      </section>
    }

    @if (sectionFlags().showTransactionMetrics && transactions()) {
      <section class="relative mb-6 overflow-hidden rounded-xl bg-linear-to-br from-primary to-brand p-5 text-white after:pointer-events-none after:absolute after:-top-20 after:-right-20 after:size-64 after:rounded-full after:bg-white/10 after:blur-2xl after:content-[''] md:p-8">
        <div class="relative z-1">
          <div class="mb-7 flex items-center justify-between gap-4">
            <div>
              <h2 class="m-0 text-[0.71rem] font-extrabold tracking-widest uppercase">Successful Transaction Volumes — {{ getPeriodLabel() }}</h2>
              <p class="m-0 mt-1 text-[0.64rem] opacity-85">Live data across all integrated nodes</p>
            </div>
            <div class="flex items-center gap-2">
              <button type="button" class="inline-flex size-8 cursor-pointer items-center justify-center rounded-full border border-white/20 bg-white/10 font-extrabold text-white hover:bg-white/20" (click)="prevMetricCard()" aria-label="Previous metric">
                <ng-icon name="lucideChevronLeft" size="18" />
              </button>
              <span class="min-w-22 rounded-full bg-white/20 px-4 py-1.5 text-center text-[0.625rem] font-extrabold tracking-wider backdrop-blur-sm">{{ activeMetricCard()?.label ?? 'DEPOSIT' }}</span>
              <button type="button" class="inline-flex size-8 cursor-pointer items-center justify-center rounded-full border border-white/20 bg-white/10 font-extrabold text-white hover:bg-white/20" (click)="nextMetricCard()" aria-label="Next metric">
                <ng-icon name="lucideChevronRight" size="18" />
              </button>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-12">
            @for (entry of successfulVolumeEntries(); track $index) {
              <div class="flex min-w-0 flex-col gap-4 md:not-first:border-l md:not-first:border-white/10 md:not-first:pl-12">
                <div class="flex items-center gap-2">
                  <div class="grid size-8 shrink-0 place-items-center rounded-full bg-white/20 text-[0.625rem] font-black text-white">{{ volumeIconLabel(entry) }}</div>
                  <div>
                    <div class="text-xs leading-tight font-bold text-white/90 uppercase">{{ volumeTitleDisplay(entry) }}</div>
                    @if (!isVolumePlaceholder(entry)) {
                      <div class="text-[0.66rem] tracking-wider text-white/95 uppercase">{{ entry.code }}</div>
                    }
                  </div>
                </div>
                <div class="flex flex-col gap-1">
                  <div class="text-[1.55rem] leading-tight font-black tracking-tight">{{ formatVolumeAmount(entry) }}</div>
                  <div class="text-[0.6875rem] font-medium text-white/80">{{ entry.count | number: '1.0-0' }} Transactions</div>
                </div>
              </div>
            }
          </div>
        </div>
      </section>
    }

    @if (sectionFlags().showTransactionHealth && statusBreakdown(); as sb) {
      <section class="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <ui-card [padded]="false">
          <div class="flex items-center justify-between gap-2.5 bg-linear-to-br from-primary to-brand px-6 py-4 text-white">
            <span class="text-[0.83rem] font-extrabold tracking-wider uppercase">Deposits Health</span>
            <ui-badge variant="warning">{{ alerts()?.deposits?.pending ?? 0 }} pending</ui-badge>
          </div>
          <div class="p-6">
            <div class="mb-3 flex flex-col items-start justify-between gap-4 min-[1201px]:flex-row min-[1201px]:items-center">
              <div class="grid size-[7.6rem] place-items-center rounded-full bg-[conic-gradient(var(--c-primary)_calc(var(--progress)*1%),var(--c-surface-container)_0)]" [style.--progress]="getSuccessPercent(sb.deposits)">
                <div class="grid size-[5.9rem] place-items-center rounded-full bg-card text-center">
                  <div>
                    <p class="m-0 text-[1.4rem] font-black text-fg">{{ sumStatusCounts(sb.deposits) }}</p>
                    <p class="m-0 text-[0.62rem] font-bold text-muted uppercase">In Period</p>
                  </div>
                </div>
              </div>
              <div class="flex flex-col gap-2.5 text-xs font-bold text-fg">
                <span class="inline-flex items-center gap-1.5"><span class="size-[0.45rem] rounded-full bg-success"></span>{{ getSuccessfulCount(sb.deposits) }} Approved</span>
                <span class="inline-flex items-center gap-1.5"><span class="size-[0.45rem] rounded-full bg-warning"></span>{{ getPendingPipelineCount(sb.deposits) }} In Queue</span>
              </div>
            </div>
            <a [routerLink]="shellRoutes.commands('manage', 'deposits')" class="text-xs font-bold text-primary no-underline">View deposits →</a>
          </div>
        </ui-card>
        <ui-card [padded]="false">
          <div class="flex items-center justify-between gap-2.5 bg-linear-to-br from-primary to-brand px-6 py-4 text-white">
            <span class="text-[0.83rem] font-extrabold tracking-wider uppercase">Payouts Health</span>
            <ui-badge variant="warning">{{ alerts()?.payouts?.pending ?? 0 }} pending</ui-badge>
          </div>
          <div class="p-6">
            <div class="mb-3 flex flex-col items-start justify-between gap-4 min-[1201px]:flex-row min-[1201px]:items-center">
              <div class="grid size-[7.6rem] place-items-center rounded-full bg-[conic-gradient(var(--c-primary)_calc(var(--progress)*1%),var(--c-surface-container)_0)]" [style.--progress]="getSuccessPercent(sb.payouts)">
                <div class="grid size-[5.9rem] place-items-center rounded-full bg-card text-center">
                  <div>
                    <p class="m-0 text-[1.4rem] font-black text-fg">{{ sumStatusCounts(sb.payouts) }}</p>
                    <p class="m-0 text-[0.62rem] font-bold text-muted uppercase">In Period</p>
                  </div>
                </div>
              </div>
              <div class="flex flex-col gap-2.5 text-xs font-bold text-fg">
                <span class="inline-flex items-center gap-1.5"><span class="size-[0.45rem] rounded-full bg-success"></span>{{ getSuccessfulCount(sb.payouts) }} Approved</span>
                <span class="inline-flex items-center gap-1.5"><span class="size-[0.45rem] rounded-full bg-warning"></span>{{ getPendingPipelineCount(sb.payouts) }} In Queue</span>
              </div>
            </div>
            <a [routerLink]="shellRoutes.commands('manage', 'payouts')" class="text-xs font-bold text-primary no-underline">View payouts →</a>
          </div>
        </ui-card>
      </section>
    }

    @if (sectionFlags().showCharts && charts(); as chartData) {
      @if (chartData.daily.length > 0) {
        <section class="mb-6 rounded-xl border border-border bg-card p-3.5 shadow-sm">
          <app-dashboard-transaction-charts
            [daily]="chartData.daily"
            [transactionTypes]="transactionTypesSummary()"
            [chartTitle]="getChartTitle()"
            [showDirectDebit]="showDirectDebitSeries()"
          />
        </section>
      }
    }

    <section class="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
      @if (sectionFlags().showWalletBalancesPanel && walletBalances(); as wb) {
        <ui-card [padded]="false">
          <div class="flex items-center justify-between gap-2.5 bg-linear-to-br from-primary to-brand px-6 py-4 text-white">
            <h2 class="m-0 text-[0.83rem] font-extrabold tracking-wider uppercase">System Wallets Balance</h2>
            <span class="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold text-white">{{ wb.total_wallets }} TOTAL</span>
          </div>
          <div class="p-6">
            <ul class="m-0 flex list-none flex-col gap-6 p-0">
              @for (row of wb.by_currency; track row.currency__code) {
                <li class="grid grid-cols-[3rem_1fr_auto] items-center gap-2.5">
                  <span class="text-[0.68rem] font-bold tracking-wide text-muted uppercase">{{ row.currency__code }}</span>
                  <div class="h-2 overflow-hidden rounded-full bg-surface-muted">
                    <div class="h-full rounded-full bg-primary" [style.width.%]="getTypeBarPercent(row.wallet_count, getMaxWalletCount())"></div>
                  </div>
                  <span class="text-[0.73rem] font-extrabold text-fg">{{ formatAmount(row.total_balance) }}</span>
                </li>
              }
            </ul>
            <a [routerLink]="shellRoutes.commands('manage', 'wallets')" class="mt-4 inline-block text-xs font-bold text-primary no-underline">View all wallets →</a>
          </div>
        </ui-card>
      }

      @if (sectionFlags().showTransactionTypesPanel) {
        <ui-card [padded]="false">
          <div class="flex items-center justify-between gap-2.5 bg-linear-to-br from-primary to-brand px-6 py-4 text-white">
            <h2 class="m-0 text-[0.83rem] font-extrabold tracking-wider uppercase">Successful Transaction Types</h2>
            <span class="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold text-white">{{ getPeriodLabel() }}</span>
          </div>
          <div class="p-6">
            <ul class="m-0 flex list-none flex-col gap-5 p-0">
              @for (row of transactionTypesSummary(); track row.name; let i = $index) {
                <li>
                  <div class="flex justify-between text-[0.625rem] font-extrabold tracking-wide text-muted uppercase">
                    <span>{{ row.name }}</span>
                    <span class="font-black text-fg">
                      {{ getTransactionTypesTotal() > 0 ? ((100 * row.value) / getTransactionTypesTotal() | number: '1.0-1') : '0' }}%
                    </span>
                  </div>
                  <div class="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface-muted">
                    <div class="h-full rounded-full" [style.width.%]="getTypeBarPercent(row.value, getTransactionTypesTotal())" [style.background]="getTypeBarColor(i)"></div>
                  </div>
                </li>
              }
            </ul>
          </div>
        </ui-card>
      }
    </section>

    @if (sectionFlags().showRecentActivity && recentActivity(); as activity) {
      <section class="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <ui-card [padded]="false">
          <div class="flex items-center justify-between gap-2.5 border-b border-border bg-surface-muted px-6 py-4">
            <h3 class="m-0 text-[0.77rem] font-extrabold uppercase">Recent Incoming</h3>
            <a [routerLink]="shellRoutes.commands('manage', 'deposits')" class="text-xs font-bold text-primary no-underline">View all deposits</a>
          </div>
          <div class="border-b border-border bg-linear-to-br from-success/10 to-transparent p-6">
            <div class="mb-4 flex flex-col items-start gap-4 md:flex-row">
              <div class="grid size-12 shrink-0 place-items-center rounded-xl bg-success-bg text-success">
                <ng-icon name="lucideTrendingUp" size="28" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="m-0 text-2xl leading-none font-black tracking-tight text-success">{{ getSummarySuccessRatio(activity.incoming_summary) }}</p>
                <p class="m-0 mt-1 text-[0.625rem] font-bold tracking-wider text-muted uppercase">Successful Transactions</p>
              </div>
              <div class="flex w-full shrink-0 justify-between gap-8 md:w-auto md:justify-start">
                <div class="text-left md:text-right">
                  <p class="m-0 text-lg leading-tight font-black text-fg">{{ activity.incoming_summary?.count ?? 0 }}</p>
                  <p class="m-0 mt-1 text-[0.5625rem] font-bold tracking-wide text-muted uppercase">Transactions</p>
                </div>
                <div class="text-left md:text-right">
                  <p class="m-0 text-lg leading-tight font-black text-fg">{{ formatAmount(activity.incoming_summary?.estimated_fees) }}</p>
                  <p class="m-0 mt-1 text-[0.5625rem] font-bold tracking-wide text-muted uppercase">Est. Fees</p>
                </div>
              </div>
            </div>
            <div class="flex flex-col gap-3">
              <div class="h-1 w-full overflow-hidden rounded-full bg-surface-muted">
                <div class="h-full rounded-full bg-success transition-[width]" [style.width.%]="getActivitySuccessPercent(activity.incoming_summary)"></div>
              </div>
              <div class="flex flex-wrap items-center justify-between gap-2">
                <span class="text-[0.625rem] font-bold text-muted">{{ getActivitySuccessLabel(activity.incoming_summary) }}</span>
                <div class="ml-auto flex flex-wrap justify-start gap-2 md:justify-end">
                  @for (entry of toCurrencyEntries(activity.incoming_summary?.success_amount_by_currency); track entry.code) {
                    <ui-badge variant="info" class="text-[0.625rem] uppercase">{{ entry.code }} {{ formatAmount(entry.amount) }}</ui-badge>
                  }
                </div>
              </div>
            </div>
          </div>
          <div>
            @for (item of activity.incoming; track item.id) {
              <a class="flex items-center justify-between gap-4 px-6 py-4 text-inherit no-underline hover:bg-row-hover" [routerLink]="getTransactionRoute(item)">
                <div class="flex min-w-0 flex-1 items-center gap-3">
                  @if (item.financial_entity_logo_url) {
                    <div class="grid size-8 shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-card p-0.5">
                      <img class="size-full object-contain" [src]="item.financial_entity_logo_url" alt="" />
                    </div>
                  } @else {
                    <div class="grid size-8 shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-surface-muted text-[0.56rem] font-black text-fg-secondary">{{ getEntityInitials(item) }}</div>
                  }
                  <div class="flex min-w-0 flex-col gap-0.5">
                    <span class="text-[0.74rem] font-extrabold text-fg">{{ item.internal_reference ?? '—' }}</span>
                    <span class="text-[0.625rem] text-muted"
                      >{{ item.financial_entity__name ?? item.financial_entity__code ?? '—' }} • {{ item.created_at | date: 'medium' }}</span
                    >
                  </div>
                </div>
                <div class="flex flex-col items-end gap-1">
                  <span class="text-[0.73rem] font-extrabold text-success">+{{ formatAmount(item.amount) }} {{ item.currency__code }}</span>
                  <ui-badge [variant]="getStatusVariant(item.status ?? '')" class="text-[0.59rem] uppercase">{{ item.status }}</ui-badge>
                </div>
              </a>
            }
          </div>
        </ui-card>
        <ui-card [padded]="false">
          <div class="flex items-center justify-between gap-2.5 border-b border-border bg-surface-muted px-6 py-4">
            <h3 class="m-0 text-[0.77rem] font-extrabold uppercase">Recent Outgoing</h3>
            <a [routerLink]="shellRoutes.commands('manage', 'payouts')" class="text-xs font-bold text-primary no-underline">View all payouts</a>
          </div>
          <div class="border-b border-border bg-linear-to-br from-danger/10 to-transparent p-6">
            <div class="mb-4 flex flex-col items-start gap-4 md:flex-row">
              <div class="grid size-12 shrink-0 place-items-center rounded-xl bg-danger-bg text-danger">
                <ng-icon name="lucideTrendingDown" size="28" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="m-0 text-2xl leading-none font-black tracking-tight text-danger">{{ getSummarySuccessRatio(activity.outgoing_summary) }}</p>
                <p class="m-0 mt-1 text-[0.625rem] font-bold tracking-wider text-muted uppercase">Successful Transactions</p>
              </div>
              <div class="flex w-full shrink-0 justify-between gap-8 md:w-auto md:justify-start">
                <div class="text-left md:text-right">
                  <p class="m-0 text-lg leading-tight font-black text-fg">{{ activity.outgoing_summary?.count ?? 0 }}</p>
                  <p class="m-0 mt-1 text-[0.5625rem] font-bold tracking-wide text-muted uppercase">Transactions</p>
                </div>
                <div class="text-left md:text-right">
                  <p class="m-0 text-lg leading-tight font-black text-fg">{{ formatAmount(activity.outgoing_summary?.estimated_fees) }}</p>
                  <p class="m-0 mt-1 text-[0.5625rem] font-bold tracking-wide text-muted uppercase">Est. Fees</p>
                </div>
              </div>
            </div>
            <div class="flex flex-col gap-3">
              <div class="h-1 w-full overflow-hidden rounded-full bg-surface-muted">
                <div class="h-full rounded-full bg-danger transition-[width]" [style.width.%]="getActivitySuccessPercent(activity.outgoing_summary)"></div>
              </div>
              <div class="flex flex-wrap items-center justify-between gap-2">
                <div class="flex flex-wrap justify-start gap-2 md:justify-end">
                  <ui-badge variant="danger" class="text-[0.5625rem]">{{ activity.outgoing_summary?.payouts ?? 0 }} payouts</ui-badge>
                  <ui-badge variant="danger" class="text-[0.5625rem]">{{ activity.outgoing_summary?.refunds ?? 0 }} refunds</ui-badge>
                  <ui-badge variant="danger" class="text-[0.5625rem]">{{ activity.outgoing_summary?.settlements ?? 0 }} settlements</ui-badge>
                  @for (entry of toCurrencyEntries(activity.outgoing_summary?.success_amount_by_currency); track entry.code) {
                    <ui-badge variant="info" class="text-[0.625rem] uppercase">{{ entry.code }} {{ formatAmount(entry.amount) }}</ui-badge>
                  }
                </div>
                <span class="text-[0.625rem] font-bold text-muted">{{ getActivitySuccessLabel(activity.outgoing_summary) }}</span>
              </div>
            </div>
          </div>
          <div>
            @for (item of activity.outgoing; track item.id) {
              <a class="flex items-center justify-between gap-4 px-6 py-4 text-inherit no-underline hover:bg-row-hover" [routerLink]="getTransactionRoute(item)">
                <div class="flex min-w-0 flex-1 items-center gap-3">
                  @if (item.financial_entity_logo_url) {
                    <div class="grid size-8 shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-card p-0.5">
                      <img class="size-full object-contain" [src]="item.financial_entity_logo_url" alt="" />
                    </div>
                  } @else {
                    <div class="grid size-8 shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-surface-muted text-[0.56rem] font-black text-fg-secondary">{{ getEntityInitials(item) }}</div>
                  }
                  <div class="flex min-w-0 flex-col gap-0.5">
                    <span class="text-[0.74rem] font-extrabold text-fg">{{ item.internal_reference ?? '—' }}</span>
                    <span class="text-[0.625rem] text-muted"
                      >{{ item.financial_entity__name ?? item.financial_entity__code ?? '—' }} • {{ item.created_at | date: 'medium' }}</span
                    >
                  </div>
                </div>
                <div class="flex flex-col items-end gap-1">
                  <span class="text-[0.73rem] font-extrabold text-danger-fg">-{{ formatAmount(item.amount) }} {{ item.currency__code }}</span>
                  <ui-badge [variant]="getStatusVariant(item.status ?? '')" class="text-[0.59rem] uppercase">{{ item.status }}</ui-badge>
                </div>
              </a>
            }
          </div>
        </ui-card>
      </section>
    }
  }
</section>
